import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EnvService } from 'src/app/env.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class MySpaceService {
  private userSource = new Subject<any>();
  private viewSource = new Subject<any>();
  private lectureCheckEventSource = new Subject<any>();
  private navigationEventSource = new Subject<any>();
  private courseListEventSource = new Subject<any>();
  private blogListEventSource = new Subject<any>();

  private coursePermissionEventSource = new Subject<any>();
  private lectureProgressSource = new BehaviorSubject<any>(undefined);
  private courseLectureStatusSource = new BehaviorSubject<any>(undefined);
  private canViewContentSource = new BehaviorSubject<boolean>(true);
  // FIREBASE_FUNCTIONS_URL = environment.firebaseFunctionsURL;

  coursePermissions$ = this.coursePermissionEventSource.asObservable();

  lectureProgress$ = this.lectureProgressSource.asObservable();
  blogEvent$ = this.blogListEventSource.asObservable();
  courseEvent$ = this.courseListEventSource.asObservable();

  navigationEvents$ = this.navigationEventSource.asObservable();
  lectureCheckEvent$ = this.lectureCheckEventSource.asObservable();
  user$ = this.userSource.asObservable();
  view$ = this.viewSource.asObservable();
  courseLectureStatus$ = this.courseLectureStatusSource.asObservable();
  canViewContent$ = this.canViewContentSource.asObservable();
  subscription;

  passCoursePermissionMap(map) {
    this.coursePermissionEventSource.next(map);
  }
  passBlogListEvent(event) {
    this.blogListEventSource.next(event);
  }
  passCourseListEvent(event) {
    this.courseListEventSource.next(event);
  }
  passLectureProgress(event) {
    this.lectureProgressSource.next(event);
  }
  passNavigationEvent(event) {
    this.navigationEventSource.next(event);
  }
  passLectureEvent(event) {
    this.lectureCheckEventSource.next(event);
  }
  passUser(user) {
    this.userSource.next(user);
  }
  passView(view) {
    this.viewSource.next(view);
  }
  passCourseLectureStatus(obj) {
    this.courseLectureStatusSource.next(obj);
  }
  setCanViewContent(value) {
    this.canViewContentSource.next(value);
  }
  firestore;
  constructor(
    private http: HttpClient,
    private envService: EnvService
  ) {
    this.firestore = inject(Firestore);
  }

  isPlanFree(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.subscription) {
          this.subscription = await firstValueFrom(this.getSubscriptionData());
        }

        // No subscription data means free plan
        if (!this.subscription) {
          resolve(true);
          return;
        }

        // New tenant-based structure: check type directly
        if (this.subscription.type === 'free') {
          resolve(true);
          return;
        }

        // If it has a Stripe subscription (trialing or active), it's not free
        if (this.subscription.stripeSubscriptionId) {
          resolve(false);
          return;
        }

        // Legacy fallback: check items/plan metadata
        const item = this.subscription.items ? this.subscription.items.find((item) => item.plan.active) : this.subscription.item;
        if (item) {
          const plan = item.plan;
          resolve(plan?.metadata?.name == 'free_monthly');
          return;
        }

        resolve(true);
      } catch (err) {
        // If we can't determine the plan, default to free (show limited menu)
        resolve(true);
      }
    });
  }

  getTranslateTTSUsage(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/getTranslateTTSUsage', httpOptions);
  }

  getSubscriptionData(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/getSubscriptionData', httpOptions);
  }

  countActiveUsers(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/countActiveUsers', httpOptions);
  }

  getAPIUsageData(data): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/getAPIUsageData/' + data.pageIndex + '/' + data.pageSize, httpOptions);
  }

  getSavedCustomizeAppTab() {
    return localStorage.getItem('personalizeAppTab') || 'logo';
  }

  getOngoingUserJourney(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/getOngoingUserJourney', httpOptions);
  }

  getUserCourseJourney(courseID): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/userJourneyByCourse/?courseID=' + courseID, httpOptions);
  }

  acceptCourseRequest(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/accept-course-request', data, httpOptions);
  }

  cancelRequest(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/cancel-request', data, httpOptions);
  }

  removeFromRessource(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/remove-access', data, httpOptions);
  }

  setUserRole(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/set-user-role', data, httpOptions);
  }

  setPrivateValue(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/set-private-value', data, httpOptions);
  }

  getPublicCourse(shareToken: string, sessionToken?: string): Observable<any> {
    let url = this.envService.apiUrl + `course/shared/${shareToken}`;
    if (sessionToken) {
      url += `?sessionToken=${encodeURIComponent(sessionToken)}`;
    }
    return this.http.get(url, httpOptions);
  }

  getPublicContent(shareToken: string, contentId: string): Observable<any> {
    return this.http.get(this.envService.apiUrl + `course/shared/${shareToken}/content/${contentId}`, httpOptions);
  }

  verifySharedAccess(shareToken: string, email: string, password: string): Observable<any> {
    return this.http.post(this.envService.apiUrl + `course/shared/${shareToken}/verify-access`, { email, password }, httpOptions);
  }

  sendShareInvites(courseID: string, password: string, shareLink: string): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/send-share-invites', { courseID, password, shareLink }, httpOptions);
  }

  getRessourceUsers(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/get-ressource-users', data, httpOptions);
  }

  deleteCurrentLogo(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'application/delete-current-logo', data, httpOptions);
  }

  deleteCurrentImage(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'application/delete-current-image', data, httpOptions);
  }

  replaceFileWithTemp(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'application/replace-image-with-temp', data, httpOptions);
  }

  addToRessource(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/give-access', data, httpOptions);
  }

  rejectCourseRequest(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'course/reject-course-request', data, httpOptions);
  }

  getCoursePermissionsMap(courseID, caller?): Observable<any> {
    return this.http.get(this.envService.apiUrl + `course/course-permissions-map?courseID=${courseID}&caller=${caller}`, httpOptions);
  }

  getCoursePermissions(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'course/course-permissions', httpOptions);
  }

  saveUserCourseJourney(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/userCourseJourney/', data, httpOptions);
  }

  setLectureCompletion(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/setLectureCompletionStatus/', data, httpOptions);
  }

  saveUserJourney(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/userJourney', data, httpOptions);
  }

  getLectureProgress(data): Observable<any> {
    const { courseID, blogID } = data;
    return this.http.get(this.envService.apiUrl + `users/getLectureProgress/?courseID=${courseID}&blogID=${blogID}`, httpOptions);
  }

  getArchivedCourses(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/archived-courses', httpOptions);
  }
  getArchivedBlogs(): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/archived-blogs', httpOptions);
  }

  addToArchive(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/add-to-archive', data, httpOptions);
  }

  removeFromArchive(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/remove-from-archive', data, httpOptions).pipe(
      tap((res) => {
        if (data.courseID) {
          this.passCourseListEvent({});
        }
        if (data.blogID) {
          this.passBlogListEvent({});
        }
      })
    );
  }

  getUser(id): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/?id=' + id, httpOptions);
  }

  getUserProfile(id): Observable<any> {
    return this.http.get(this.envService.apiUrl + 'users/profile/?id=' + id, httpOptions);
  }

  updateUserProfile(data): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/update/profile', data, httpOptions);
  }

  deleteAccount(credentials): Observable<any> {
    return this.http.post(this.envService.apiUrl + 'users/profile/deleteAccount', credentials, httpOptions);
  }
}
